<script lang="ts">
  // Name dialog. Mounted globally in routes/+layout.svelte and decides
  // itself whether to render based on the auth store. Replaces the retired
  // `/onboarding` route as VoiceDojo's "tell us your name" prompt.
  //
  // Trigger (wiki/feature-name-dialog.md "Trigger conditions"):
  //   1. Auth store is `authenticated` (cookie + valid whoami).
  //   2. `user.name` is null / empty on the signed-in user.
  //   3. No impersonation session is live, and the route is not the admin
  //      portal — this modal must never cover the chrome that carries the way
  //      out of an impersonated session, nor name an account on its owner's
  //      behalf.
  //
  // Google OAuth users never satisfy (2) because the OAuth callback writes
  // `name = given_name` at `UserCreatedViaOAuth` time. Magic-link
  // signups leave `user.name` empty until this dialog submits — so the dialog
  // is effectively scoped to the email auth path without needing an explicit
  // authProvider field on WhoAmIResponse. See wiki/feature-name-dialog.md
  // §"Trigger conditions" paragraph 3 — the dialog is dismissible (X / Esc
  // / outside-click) but re-evaluates on every navigation, so it returns
  // until the user submits. It renders only while open: a closed dialog left
  // in the DOM still exposes its name field to every query on the page.
  //
  // Submit → updateName() (PUT /api/user/profile, the typed
  // `ProfileUpdateCommand`). The matching
  // backend endpoint is in
  // backend/web/Roles.Web/Endpoints/Authenticated/UserProfileEndpoints.cs
  // (WolverinePut + transitional WolverinePost).
  //
  // `UserProjection` is ASYNC, so the name is NOT in the read model when the PUT
  // returns. The dialog therefore stays in its pending state after a successful
  // save and closes on the identity the read channel delivers
  // (the shared `currentUserLive` store re-runs whoami on the `User` push).
  // `PENDING_FALLBACK_MS` bounds that wait with exactly ONE extra read so a lost
  // push can never leave the user staring at a spinner.

  import { page } from '$app/stores';
  import { auth, refresh } from '$lib/stores/auth';
  import { ApiError } from '$lib/api/client';
  import { updateName } from '$lib/api/profile';
  import { TextField, Button, Notice, IconButton } from '$lib/ui';
  import { X } from '$lib/ui/icons';
  import { t } from '$lib/i18n';
  import { UiKeys } from '@voicedojo/backend-types';

  // How long to wait for the read channel before falling back to one direct
  // re-read. Long enough to cover the daemon + the hub's ~1s change batch,
  // short enough that a dropped push is not a dead dialog. One shot, never a poll.
  const PENDING_FALLBACK_MS = 5000;

  let dialogEl: HTMLDialogElement | undefined = $state(undefined);
  let name: string = $state('');
  let submitting: boolean = $state(false);
  let errorMessage: string = $state('');
  // The route a dismissal applies to — dismissal is "later, not never", so the
  // next navigation asks again.
  let dismissedOn: string | null = $state(null);

  // True when the auth store says we're signed in AND the server reports no
  // name. Drives the open/close lifecycle below.
  let shouldOpen: boolean = $derived.by(() => {
    // Checkout-claim (wiki/feature-checkout-claim.md §B / AC-CC-4):
    // when the buyer confirmed a name, the backend sets it on the new account at
    // claim time, so `user.name` is already non-empty and the trigger below keeps
    // this dialog shut — no per-tab suppression flag needed.
    if ($auth.status !== 'authenticated') return false;
    // An impersonating admin is looking at someone else's account through the
    // banner this modal would cover — and Stop is the only way out of it.
    if ($auth.user.impersonation) return false;
    // The admin portal runs its own chrome; the product's name prompt never
    // renders over it.
    if ($page.url.pathname.startsWith('/admin')) return false;
    // Guests (roles = ['Guest']) never get the name dialog — only registered
    // accounts carry the 'User' role (the role chain: User implies Guest,
    // Premium implies User, …).
    if (!$auth.user.roles.includes('User')) return false;
    const n = $auth.user.name;
    return n == null || n.trim().length === 0;
  });

  let isOpen: boolean = $derived(shouldOpen && dismissedOn !== $page.url.pathname);

  $effect(() => {
    if (dialogEl && !dialogEl.open) dialogEl.showModal();
  });

  let canSave: boolean = $derived(name.trim().length > 0 && !submitting);

  let fallbackTimer: ReturnType<typeof setTimeout> | null = null;

  function clearFallback(): void {
    if (fallbackTimer === null) return;
    clearTimeout(fallbackTimer);
    fallbackTimer = null;
  }

  // The save settled: the pushed identity carries a name, so the dialog is
  // closing. Drop the pending state and the input so a later sign-in on the
  // same browser mounts clean.
  $effect(() => {
    if (shouldOpen) return;
    clearFallback();
    submitting = false;
    name = '';
  });

  $effect(() => () => clearFallback());

  async function onSubmit(event: SubmitEvent): Promise<void> {
    event.preventDefault();
    // The dialog only opens for an authenticated user, so `userId` is always
    // present here — the guard narrows the store union rather than defending.
    const userId = $auth.status === 'authenticated' ? $auth.user.id : null;
    if (!canSave || !userId) return;
    submitting = true;
    errorMessage = '';

    try {
      await updateName(userId, name.trim());
    } catch (e) {
      submitting = false;
      if (!(e instanceof ApiError)) throw e;
      errorMessage = e.message;
      return;
    }

    // Stay pending. The read channel's `User` push re-runs whoami, `shouldOpen`
    // flips, and the $effect above closes the dialog. The timer is the bounded
    // fallback for a push that never lands: ONE direct re-read, and if the name
    // still isn't there the form goes usable again rather than spinning forever.
    clearFallback();
    fallbackTimer = setTimeout(() => {
      fallbackTimer = null;
      void refresh().finally(() => {
        if (shouldOpen) submitting = false;
      });
    }, PENDING_FALLBACK_MS);
  }

  function handleClose(): void {
    // X and backdrop route through the same native close event Esc fires, so
    // every dismissal lands on handleNativeClose.
    if (dialogEl?.open) dialogEl.close();
    else dismissedOn = $page.url.pathname;
  }

  function handleNativeClose(): void {
    dismissedOn = $page.url.pathname;
    // Reset transient error text so a re-open doesn't show stale messaging.
    errorMessage = '';
  }

  function handleBackdropClick(event: MouseEvent): void {
    // Native <dialog> doesn't auto-close on backdrop click. Detect the
    // backdrop-vs-content boundary by checking whether the click target is
    // the dialog element itself (not a child of the inner panel).
    if (event.target === dialogEl) {
      handleClose();
    }
  }
</script>

{#if isOpen}
  <dialog
    bind:this={dialogEl}
    class="name-dialog"
    aria-labelledby="name-dialog-title"
    onclose={handleNativeClose}
    onclick={handleBackdropClick}
    data-testid="name-dialog"
  >
    <div class="panel">
      <header>
        <h2 id="name-dialog-title">{t(UiKeys.NameDialog.Heading)}</h2>
        <IconButton label={t(UiKeys.NameDialog.DismissAria)} icon={X} size="sm" onclick={handleClose} />
      </header>

      <form onsubmit={onSubmit} aria-busy={submitting}>
        <TextField
          label={t(UiKeys.NameDialog.NameLabel)}
          bind:value={name}
          autocomplete="name"
          required
          placeholder={t(UiKeys.NameDialog.NamePlaceholder)}
          disabled={submitting}
          data-testid="name-input"
        />

        {#if errorMessage}
          <Notice tone="error">{errorMessage}</Notice>
        {/if}

        <Button
          type="submit"
          variant="primary"
          disabled={!canSave}
          aria-label={t(UiKeys.NameDialog.SaveAria)}
          data-testid="name-save"
        >
          {submitting ? t(UiKeys.NameDialog.Saving) : t(UiKeys.NameDialog.Save)}
        </Button>
      </form>
    </div>
  </dialog>
{/if}

<style lang="scss">
  .name-dialog {
    border: none;
    border-radius: 12px;
    padding: 0;
    background: transparent;
    max-width: 28rem;
    width: 90vw;
  }

  .name-dialog::backdrop {
    background: rgb(0 0 0 / 0.45);
  }

  .panel {
    background: var(--paper);
    border: 1px solid var(--line);
    border-radius: 12px;
    padding: 1.5rem 1.5rem 1.75rem;
    font-family: var(--font-ui);
    color: var(--ink);
    box-shadow: 0 12px 36px rgb(0 0 0 / 0.2);
  }

  header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 1.25rem;
  }

  h2 {
    margin: 0;
    font-size: var(--text-guide);
    line-height: 1.35;
    color: var(--ink);
  }

  form {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  /* The Save button is a block-start action — nudge it down a touch from the
     last field. Visual styling lives in the Button primitive. */
  form :global(.vd-btn) {
    margin-top: 0.25rem;
    align-self: flex-start;
  }
</style>
